//@ts-nocheck
import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaTimes } from 'react-icons/fa';
import { Menu } from 'lucide-react'; // Using Lucide's hamburger menu icon
import logo from "../../assets/icon.png";
import { AnimatePresence, motion } from 'framer-motion';
import VoiceFirstChatInterface from '../MobileChatInterface'; 
import { Mic } from 'lucide-react';

const MobileHeader: React.FC = () => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [activeSubmenu, setActiveSubmenu] = useState("");
    const [isChatOpen, setIsChatOpen] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);

    // Close menu when clicking outside
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setIsMenuOpen(false);
            }
        }
        
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const toggleMenu = () => {
        setIsMenuOpen(!isMenuOpen);
        // Close chat if menu is opening
        if (!isMenuOpen && isChatOpen) {
            setIsChatOpen(false);
        }
    };

    const toggleSubmenu = (menu: string) => {
        setActiveSubmenu(activeSubmenu === menu ? "" : menu);
    };

    const toggleChat = (e) => {
        e.preventDefault();
        setIsChatOpen(!isChatOpen);
        // Close menu if chat is opening
        if (!isChatOpen && isMenuOpen) {
            setIsMenuOpen(false);
        }
    };

    return (
        <>
            {/* Header - Keeping original bg-black and height */}
            <div className="fixed w-full z-[99] top-4 left-1/2 -translate-x-1/2 flex justify-center">
                <div className="inline-block w-[300px] xs:w-[340px] relative" ref={menuRef}>
                    {/* Preserving original header styling */}
                    <div className="flex items-center justify-between p-2 rounded-[16px] bg-black gap-3 shadow-lg relative">
                        {/* Centered Logo with Refined Pulse Animation - Keeping original */}
                        <div className="absolute left-1/2 transform -translate-x-1/2 flex items-center justify-center">
                            {/* Very subtle outer pulse ring with border only */}
                            <div className="absolute w-11 h-11 rounded-[10px] border border-white opacity-40 animate-ping"></div>
                            
                            {/* Second pulse ring with even lighter opacity */}
                            <div className="absolute w-11 h-11 rounded-[10px] border border-white opacity-20 animate-pulse"></div>

                            {/* Subtle white glow behind the logo */}
                            <div className="absolute w-10 h-10 rounded-[10px] bg-white bg-opacity-10 blur-sm"></div>
                            
                            {/* The actual logo link */}
                            <Link
                                to="/"
                                className="relative z-10 transition-transform duration-300 hover:scale-105"
                                onClick={toggleChat}
                                aria-label="Open chat"
                            >
                                <div className="relative">
                                    {/* Logo */}
                                    <img
                                        src={logo}
                                        alt="Logo"
                                        width={40}
                                        height={40}
                                        className="rounded-[10px]"
                                    />
                                    
                                    {/* Chat Bubble Icon */}
                                    <div className="absolute top-[-8px] right-[-12px] p-2">
                                        <Mic size={16} color="#ffffff" />
                                    </div>
                                </div>
                            </Link>
                        </div>

                        {/* Menu Toggle Icon - More obvious hamburger menu */}
                        <button
                            onClick={toggleMenu}
                            className="text-white text-lg ml-auto pr-1"
                            aria-label="Toggle menu"
                        >
                            {isMenuOpen ? <FaTimes size={24} /> : <Menu size={24} />}
                        </button>
                    </div>

                    {/* Dropdown Menu with Frosted Glass Effect */}
                    <AnimatePresence>
                        {isMenuOpen && (
                            <motion.div
                                initial={{ opacity: 0, y: -10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -10 }}
                                transition={{ duration: 0.2 }}
                                className="absolute right-0 mt-2 w-64 rounded-lg bg-black/60 backdrop-blur-xl border border-white/10 shadow-xl overflow-hidden z-[100]"
                            >
                                <div className="p-4 space-y-3">
                                    {/* Company Menu */}
                                    <div>
                                        <div
                                            className="flex items-center justify-between cursor-pointer text-white p-2 hover:bg-white/10 rounded-md transition-colors"
                                            onClick={() => toggleSubmenu("company")}
                                        >
                                            <p className="font-medium">Company</p>
                                            <svg 
                                                xmlns="http://www.w3.org/2000/svg" 
                                                width="16" 
                                                height="16" 
                                                viewBox="0 0 24 24" 
                                                fill="none" 
                                                stroke="currentColor" 
                                                strokeWidth="2" 
                                                strokeLinecap="round" 
                                                strokeLinejoin="round"
                                                className={`transition-all duration-300 ${
                                                    activeSubmenu === "company" ? "rotate-180" : "rotate-0"
                                                }`}
                                            >
                                                <polyline points="6 9 12 15 18 9"></polyline>
                                            </svg>
                                        </div>
                                        <AnimatePresence>
                                            {activeSubmenu === "company" && (
                                                <motion.div
                                                    initial={{ height: 0, opacity: 0 }}
                                                    animate={{ height: "auto", opacity: 1 }}
                                                    exit={{ height: 0, opacity: 0 }}
                                                    transition={{ duration: 0.2 }}
                                                    className="ml-2 mt-1 space-y-1 overflow-hidden"
                                                >
                                                    <Link to="/about-futeur" className="block text-white/90 hover:text-white p-2 pl-4 rounded-md hover:bg-white/10 transition-colors">
                                                        About
                                                    </Link>
                                                    <Link to="/careers" className="block text-white/90 hover:text-white p-2 pl-4 rounded-md hover:bg-white/10 transition-colors">
                                                        Careers
                                                    </Link>
                                                    <Link to="/legal" className="block text-white/90 hover:text-white p-2 pl-4 rounded-md hover:bg-white/10 transition-colors">
                                                        Legal
                                                    </Link>
                                                    <Link to="/vision" className="block text-white/90 hover:text-white p-2 pl-4 rounded-md hover:bg-white/10 transition-colors">
                                                        Vision
                                                    </Link>
                                                    <Link to="/contact" className="block text-white/90 hover:text-white p-2 pl-4 rounded-md hover:bg-white/10 transition-colors">
                                                        Contact
                                                    </Link>
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </div>

                                    {/* Products Menu */}
                                    <div>
                                        <div
                                            className="flex items-center justify-between cursor-pointer text-white p-2 hover:bg-white/10 rounded-md transition-colors"
                                            onClick={() => toggleSubmenu("products")}
                                        >
                                            <p className="font-medium">Products</p>
                                            <svg 
                                                xmlns="http://www.w3.org/2000/svg" 
                                                width="16" 
                                                height="16" 
                                                viewBox="0 0 24 24" 
                                                fill="none" 
                                                stroke="currentColor" 
                                                strokeWidth="2" 
                                                strokeLinecap="round" 
                                                strokeLinejoin="round"
                                                className={`transition-all duration-300 ${
                                                    activeSubmenu === "products" ? "rotate-180" : "rotate-0"
                                                }`}
                                            >
                                                <polyline points="6 9 12 15 18 9"></polyline>
                                            </svg>
                                        </div>
                                        <AnimatePresence>
                                            {activeSubmenu === "products" && (
                                                <motion.div
                                                    initial={{ height: 0, opacity: 0 }}
                                                    animate={{ height: "auto", opacity: 1 }}
                                                    exit={{ height: 0, opacity: 0 }}
                                                    transition={{ duration: 0.2 }}
                                                    className="ml-2 mt-1 space-y-1 overflow-hidden"
                                                >
                                                    <Link to="/FuteurCred" className="block text-white/90 hover:text-white p-2 pl-4 rounded-md hover:bg-white/10 transition-colors">
                                                        FuteurCred
                                                    </Link>
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </div>

                                    {/* Links */}
                                    <div className="pt-2 border-t border-white/10 mt-2">
                                        <Link
                                            to="https://app.futeur.ai/signup"
                                            className="block w-full py-2 mt-2 text-center bg-white text-black rounded-lg hover:bg-white/90 transition-colors font-medium"
                                        >
                                            Sign up
                                        </Link>
                                        <Link
                                            to="https://app.futeur.ai/signin"
                                            className="block w-full mt-2 py-2 text-center bg-transparent text-white rounded-lg border border-white/20 hover:bg-white/10 transition-colors"
                                        >
                                            Log in
                                        </Link>
                                    </div>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </div>

            <div className="fixed w-full z-[98] top-[54px] flex justify-center">
                <div className="backdrop-blur-md bg-white/60 px-3 py-1 rounded-md shadow-sm">
                    <p className="text-black text-xs font-semibold transition-all duration-1000 animate-pulse">
                    Press icon to enable chatbot
                    </p>
                </div>
            </div>

            {/* Chat Interface */}
            <AnimatePresence>
                {isChatOpen && (
                    <VoiceFirstChatInterface 
                        isOpen={isChatOpen} 
                        onClose={() => setIsChatOpen(false)} 
                    />
                )}
            </AnimatePresence>
        </>
    );
};

export default MobileHeader;